import type { FC } from 'react'
import type { ChatMessage } from '@/constants/home'

import { useEffect, useState } from 'react'
import {
    CheckCheck,
    Flame,
    Heart,
    Laugh,
    PartyPopper
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { chatThreads } from '@/constants/home'

const reactionIcons = {
    heart: Heart,
    fire: Flame,
    laugh: Laugh,
    clap: PartyPopper
}

const reactionNames = {
    heart: 'loved',
    fire: 'fire',
    laugh: 'laughed at',
    clap: 'celebrated'
}

const TypingBubble: FC = () => {
    return (
        <div className='pop-up flex w-fit items-center gap-1 rounded-2xl rounded-bl-md bg-paper px-4 py-3 shadow-[0_8px_18px_-14px_rgb(30_30_30/0.4)]'>
            <span className='typing-dot size-1.5 rounded-full bg-ink/60' />
            <span className='typing-dot size-1.5 rounded-full bg-ink/60' />
            <span className='typing-dot size-1.5 rounded-full bg-ink/60' />
            <span className='sr-only'>typing</span>
        </div>
    )
}

const Bubble: FC<{ message: ChatMessage, showReaction: boolean }> = ({ message, showReaction }) => {
    const ours = message.from === 'us'
    const Icon = message.reaction ? reactionIcons[message.reaction] : null

    return (
        <div className={cn('pop-up flex flex-col', ours ? 'items-end' : 'items-start')}>
            <div className={cn(
                'relative max-w-[85%] rounded-2xl px-4 py-2.5 text-[15px] leading-snug shadow-[0_8px_18px_-14px_rgb(30_30_30/0.4)]',
                ours ? 'rounded-br-md bg-ember text-ink' : 'rounded-bl-md bg-paper text-ink'
            )}>
                {message.text}
                {Icon && showReaction && (
                    <span
                        className={cn(
                            'pop-up absolute -bottom-3 flex size-7 items-center justify-center rounded-full border-2 border-cream bg-ink text-ember [animation-delay:500ms]',
                            ours ? '-left-2' : '-right-2'
                        )}
                    >
                        <Icon className='size-3.5' />
                        <span className='sr-only'>Reaction: {reactionNames[message.reaction!]}</span>
                    </span>
                )}
            </div>
            <span className='mt-1.5 px-1 font-mono text-[10px] text-stone'>{message.time}</span>
        </div>
    )
}

export const GroupChat: FC = () => {
    const [active, setActive] = useState(0)
    const [shown, setShown] = useState(0)
    const [typing, setTyping] = useState(false)
    const { ref, inView } = useInView<HTMLDivElement>({ rootMargin: '0px 0px -25% 0px' })
    const thread = chatThreads[active]

    useEffect(() => {
        if (!inView) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setShown(thread.messages.length)
            setTyping(false)

            return
        }

        const timers: Array<number> = []
        let elapsed = 300

        setShown(0)
        setTyping(false)

        thread.messages.forEach((message, index) => {
            if (message.from === 'client') {
                timers.push(window.setTimeout(() => setTyping(true), elapsed))
                elapsed += 1100
            }
            else {
                elapsed += 600
            }

            timers.push(window.setTimeout(() => {
                setTyping(false)
                setShown(index + 1)
            }, elapsed))

            elapsed += 500
        })

        return () => timers.forEach(timer => window.clearTimeout(timer))
    }, [inView, active, thread.messages])

    const lastOurs = thread.messages.reduce((last, message, index) => (message.from === 'us' ? index : last), -1)
    const finished = shown >= thread.messages.length

    return (
        <section
            id='group-chat'
            aria-labelledby='group-chat-title'
            className='py-20 md:py-28'
        >
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='13'>Testimonials, unedited</SectionLabel>
                        <SplitText
                            as='h2'
                            id='group-chat-title'
                            lines={['Group Chat']}
                            italic='chat'
                            className='mt-5 text-[clamp(3rem,7vw,6.5rem)] font-bold leading-[0.88] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        as='p'
                        delay={150}
                        className='text-lg leading-relaxed text-stone lg:col-span-5'
                    >
                        We do not ask clients for testimonials. We just screenshot the group chat, with permission. Pick a thread.
                    </Reveal>
                </div>

                <div
                    ref={ref}
                    className='mt-12 grid overflow-hidden rounded-[2rem] border border-ink/10 bg-paper md:rounded-[2.5rem] lg:grid-cols-[340px_1fr]'
                >
                    <div className='border-b border-ink/10 lg:border-b-0 lg:border-r'>
                        <p className='px-6 pb-3 pt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Chats · 3 unread</p>
                        <ul
                            role='tablist'
                            aria-label='Client chats'
                            className='flex gap-1 overflow-x-auto px-3 pb-3 lg:flex-col lg:overflow-visible'
                        >
                            {chatThreads.map((item, index) => (
                                <li
                                    key={item.id}
                                    className='shrink-0'
                                >
                                    <button
                                        type='button'
                                        role='tab'
                                        aria-selected={active === index}
                                        aria-controls='chat-panel'
                                        onClick={() => setActive(index)}
                                        className={cn(
                                            'flex w-full items-center gap-3 rounded-2xl p-3 text-left transition-colors',
                                            active === index ? 'bg-sand' : 'hover:bg-sand/60'
                                        )}
                                    >
                                        <span
                                            className='flex size-11 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold text-ink'
                                            style={{ backgroundColor: item.tone }}
                                        >
                                            {item.initials}
                                        </span>
                                        <span className='min-w-0 flex-1'>
                                            <span className='block truncate text-[15px] font-semibold'>{item.name}</span>
                                            <span className='block truncate text-sm text-stone'>{item.preview}</span>
                                        </span>
                                        {active !== index && <span className='size-2.5 shrink-0 rounded-full bg-ember' />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div
                        id='chat-panel'
                        role='tabpanel'
                        aria-label={`Chat with ${thread.name}`}
                        className='flex min-h-[520px] flex-col bg-cream/60'
                    >
                        <div className='flex items-center gap-3 border-b border-ink/10 bg-paper px-6 py-4'>
                            <span
                                className='flex size-10 items-center justify-center rounded-full font-heading text-sm font-bold'
                                style={{ backgroundColor: thread.tone }}
                            >
                                {thread.initials}
                            </span>
                            <div>
                                <p className='font-semibold leading-none'>{thread.name}</p>
                                <p className='mt-1 font-mono text-[11px] text-stone'>{thread.role} · with the Updraft team</p>
                            </div>
                        </div>

                        <div
                            aria-live='polite'
                            className='flex flex-1 flex-col justify-end gap-5 p-5 md:p-8'
                        >
                            {thread.messages.slice(0, shown).map((message, index) => (
                                <Bubble
                                    key={`${thread.id}-${index}`}
                                    message={message}
                                    showReaction
                                />
                            ))}

                            {typing && <TypingBubble />}

                            {finished && lastOurs >= 0 && (
                                <p className='pop-up flex items-center justify-end gap-1.5 font-mono text-[10px] text-stone'>
                                    <CheckCheck className='size-3.5 text-volt' />
                                    Read {thread.messages[thread.messages.length - 1].time}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
